import { supabase } from "@/integrations/supabase/client";

// ─── Types ───

export type CsmTaskType = 'call' | 'email' | 'sms' | 'operational';
export type CsmTaskStatus = 'todo' | 'in_progress' | 'done' | 'deleted';
export type CsmTaskTrigger = 'manual' | 'urgent_ticket' | 'cocooning_call' | 'booking_ppm' | 'app_download' | 'onboarding_reminder' | 'shooting_cancelled' | 'video_delay' | 'rebooking';

export interface CsmTask {
  id: string;
  title: string;
  description: string | null;
  assigned_to: string;
  client_id: string;
  due_date: string; // DATE string YYYY-MM-DD
  estimated_minutes: number;
  task_type: CsmTaskType;
  status: CsmTaskStatus;
  trigger_type: CsmTaskTrigger;
  trigger_reference_id: string | null;
  client_phone: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  client_first_name?: string | null;
  client_last_name?: string | null;
  client_avatar_url?: string | null;
  client_email?: string | null;
  client_is_vip?: boolean;
  client_health_score?: number | null;
  assigned_first_name?: string | null;
  assigned_last_name?: string | null;
}

export interface CsmTaskComment {
  id: string;
  task_id: string;
  author_id: string;
  content: string;
  created_at: string;
  author_first_name?: string | null;
  author_last_name?: string | null;
}

export interface CreateCsmTaskInput {
  title: string;
  description?: string;
  assigned_to: string;
  client_id: string;
  due_date: string;
  estimated_minutes?: number;
  task_type: CsmTaskType;
  client_phone?: string;
}

export interface UpdateCsmTaskInput {
  title?: string;
  description?: string;
  due_date?: string;
  estimated_minutes?: number;
  task_type?: CsmTaskType;
  status?: CsmTaskStatus;
  assigned_to?: string;
  client_phone?: string;
}

// ─── DAL Functions ───

type Result<T> = { success: true; data: T } | { success: false; error: string };

/**
 * Fetch all CSM tasks with client data joined
 */
export const getCsmTasks = async (filters?: {
  status?: CsmTaskStatus[];
  assignedTo?: string;
}): Promise<Result<CsmTask[]>> => {
  try {
    let query = supabase
      .from('csm_tasks')
      .select('*')
      .order('due_date', { ascending: true });

    if (filters?.status && filters.status.length > 0) {
      query = query.in('status', filters.status);
    }
    if (filters?.assignedTo) {
      query = query.eq('assigned_to', filters.assignedTo);
    }

    const { data: tasks, error } = await query;
    if (error) throw error;
    if (!tasks || tasks.length === 0) return { success: true, data: [] };

    // Gather unique user IDs for joins
    const clientIds = [...new Set(tasks.map(t => t.client_id))];
    const assignedIds = [...new Set(tasks.map(t => t.assigned_to))];
    const allUserIds = [...new Set([...clientIds, ...assignedIds])];

    // Fetch user profiles
    const { data: users } = await supabase
      .from('users')
      .select('id, first_name, last_name, avatar_url, email, is_vip_override')
      .in('id', allUserIds);

    const userMap = new Map((users || []).map(u => [u.id, u]));

    // Fetch health scores for clients
    const { data: healthScores } = await supabase
      .from('csm_health_scores')
      .select('user_id, score')
      .in('user_id', clientIds);

    const healthMap = new Map((healthScores || []).map(h => [h.user_id, h.score]));

    // Fetch followers for VIP auto
    const { data: analyticsData } = await supabase
      .from('social_account_analytics')
      .select('user_id, platform, followers_count')
      .in('user_id', clientIds)
      .neq('platform', 'linkedin');

    const maxFollowers = new Map<string, number>();
    (analyticsData || []).forEach(a => {
      const curr = maxFollowers.get(a.user_id) || 0;
      if ((a.followers_count || 0) > curr) maxFollowers.set(a.user_id, a.followers_count || 0);
    });

    // Get shooting counts for VIP auto
    const { data: shootings } = await supabase
      .from('shootings')
      .select('user_id')
      .eq('status', 'completed')
      .in('user_id', clientIds);

    const shootingCounts = new Map<string, number>();
    (shootings || []).forEach(s => {
      shootingCounts.set(s.user_id, (shootingCounts.get(s.user_id) || 0) + 1);
    });

    const result: CsmTask[] = tasks.map(t => {
      const client = userMap.get(t.client_id);
      const assigned = userMap.get(t.assigned_to);
      const override = (client as any)?.is_vip_override as boolean | null;
      const mf = maxFollowers.get(t.client_id) || 0;
      const sc = shootingCounts.get(t.client_id) || 0;
      const isVipAuto = mf >= 10000 || sc >= 3;
      const isVip = override ?? isVipAuto;

      return {
        ...t,
        client_first_name: client?.first_name || null,
        client_last_name: client?.last_name || null,
        client_avatar_url: client?.avatar_url || null,
        client_email: client?.email || null,
        client_is_vip: isVip,
        client_health_score: healthMap.get(t.client_id) ?? null,
        assigned_first_name: assigned?.first_name || null,
        assigned_last_name: assigned?.last_name || null,
      };
    });

    return { success: true, data: result };
  } catch (error) {
    console.error('Error fetching CSM tasks:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Create a new CSM task
 */
export const createCsmTask = async (input: CreateCsmTaskInput): Promise<Result<CsmTask>> => {
  try {
    const { data, error } = await supabase
      .from('csm_tasks')
      .insert({
        title: input.title,
        description: input.description || null,
        assigned_to: input.assigned_to,
        client_id: input.client_id,
        due_date: input.due_date,
        estimated_minutes: input.estimated_minutes || 15,
        task_type: input.task_type,
        client_phone: input.client_phone || null,
        trigger_type: 'manual',
      } as any)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data: data as CsmTask };
  } catch (error) {
    console.error('Error creating CSM task:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Update a CSM task
 */
export const updateCsmTask = async (taskId: string, input: UpdateCsmTaskInput): Promise<Result<CsmTask>> => {
  try {
    const { data, error } = await supabase
      .from('csm_tasks')
      .update(input as any)
      .eq('id', taskId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data: data as CsmTask };
  } catch (error) {
    console.error('Error updating CSM task:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Bulk update task status
 */
export const bulkUpdateCsmTaskStatus = async (taskIds: string[], status: CsmTaskStatus): Promise<Result<void>> => {
  try {
    const { error } = await supabase
      .from('csm_tasks')
      .update({ status } as any)
      .in('id', taskIds);

    if (error) throw error;
    return { success: true, data: undefined };
  } catch (error) {
    console.error('Error bulk updating CSM tasks:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Get comments for a task
 */
export const getCsmTaskComments = async (taskId: string): Promise<Result<CsmTaskComment[]>> => {
  try {
    const { data: comments, error } = await supabase
      .from('csm_task_comments')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    if (!comments || comments.length === 0) return { success: true, data: [] };

    const authorIds = [...new Set(comments.map(c => c.author_id))];
    const { data: users } = await supabase
      .from('users')
      .select('id, first_name, last_name')
      .in('id', authorIds);

    const userMap = new Map((users || []).map(u => [u.id, u]));

    const result: CsmTaskComment[] = comments.map(c => {
      const author = userMap.get(c.author_id);
      return {
        ...c,
        author_first_name: author?.first_name || null,
        author_last_name: author?.last_name || null,
      };
    });

    return { success: true, data: result };
  } catch (error) {
    console.error('Error fetching task comments:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Add a comment to a task
 */
export const addCsmTaskComment = async (taskId: string, authorId: string, content: string): Promise<Result<CsmTaskComment>> => {
  try {
    const { data, error } = await supabase
      .from('csm_task_comments')
      .insert({ task_id: taskId, author_id: authorId, content } as any)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data: data as CsmTaskComment };
  } catch (error) {
    console.error('Error adding task comment:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Get all CSM users (for assignment dropdown)
 */
export const getCsmUsers = async (): Promise<Result<Array<{ id: string; first_name: string | null; last_name: string | null }>>> => {
  try {
    const { data: roles } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'csm');

    if (!roles || roles.length === 0) return { success: true, data: [] };

    const { data: users, error } = await supabase
      .from('users')
      .select('id, first_name, last_name')
      .in('id', roles.map(r => r.user_id));

    if (error) throw error;
    return { success: true, data: users || [] };
  } catch (error) {
    console.error('Error fetching CSM users:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Get all customers (for client selection dropdown)
 */
export const getCustomersList = async (): Promise<Result<Array<{ id: string; first_name: string | null; last_name: string | null; email: string }>>> => {
  try {
    const { data: roles } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'customer');

    if (!roles || roles.length === 0) return { success: true, data: [] };

    const { data: users, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, email')
      .in('id', roles.map(r => r.user_id))
      .eq('is_active', true)
      .order('first_name', { ascending: true });

    if (error) throw error;
    return { success: true, data: users || [] };
  } catch (error) {
    console.error('Error fetching customers list:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};
